<script setup lang="ts">
import {provide, reactive, ref, shallowReactive, shallowReadonly, watch, watchEffect} from 'vue';
import PaneView from './PaneView.vue';
import {Pane, stateKey, Tab, TabView, Workspace} from './workspace-types';
import {v4 as uuidv4, v7} from 'uuid';
import WorkspaceSidebar from "./WorkspaceSidebar.vue";
import {contextKey, useContext, ViewContext, ViewNavigateOptions, ViewOptions} from "@hantera/portal-app";
import useWorkspace from "@/composition/useWorkspace";

const localStorageKey = 'workspace_state_v2';

const state: Workspace = reactive<Workspace>(JSON.parse(localStorage.getItem(localStorageKey) || '{}') as Workspace);
const context = useContext()

watch(state, () => {
  localStorage.setItem(localStorageKey, JSON.stringify(state));
}, {
  deep: true,
});

if (typeof state.panes === 'undefined') {
  state.panes = [];
}

if (state.panes.length === 0) {
  state.panes.push({
    paneId: uuidv4(),
    selectedTabId: 'new',
    tabs: [],
    width: 100,
  });
}

if (typeof state.showSidebar === 'undefined') {
  state.showSidebar = true;
}


// Symbols can't be serialized so we assign some new ones here the help vue
for (const pane of state.panes) {
  if (!pane.paneId) {
    pane.paneId = uuidv4();
  }
}

provide(stateKey, state);
const {openTab, getTab, getChildrenOf, closeTab, getPaneFor} = useWorkspace(state)

provide(contextKey, {
  ...context,
  openView(uri: string, options?: ViewOptions): ViewContext {
    const [path, querystring] = uri.split('?')

    const query = options?.query ?? {}
    if (querystring) {
      const params = new URLSearchParams(querystring)
      params.forEach((val, key) => {
        query[key] = val
      })
    }

    let pane: Pane | undefined;
    if (state.activePaneId) {
      pane = state.panes.find(r => r.paneId === state.activePaneId);
    }
    if (!pane) {
      pane = state.panes[0];
    }


    const tab = <TabView>{
      type: 'view',
      tabId: v7(),
      path,
      query,
      state: reactive(options?.state ?? {}),
      parentTabId: options?.parentViewId
    }
    
    openTab(pane.paneId, tab)
    
    return tabToViewContext(tab)!
  },
  getView(viewId: string | undefined): ViewContext | undefined {
    if (!viewId) {
      return
    }
    
    const tab = getTab(viewId)
    if (!tab) {
      return
    }
    
    return tabToViewContext(tab)
  },
  getChildrenViews(parentViewId?: string): Readonly<ViewContext>[] {
    if (!parentViewId) {
      return []
    }

    return getChildrenOf(parentViewId).map(r => tabToViewContext(r)!)
  },
})

function tabToViewContext(tab: Tab) {

  if (tab?.type === 'view') {
    const tabView = tab as TabView
    
    // Either we make all readonly properties computed and editable ref
    // Or we make the whole object reactive which also makes it mutable, which is not ideal
    // refs are removed when making it reactive which also breaks the readonly
    
    const result: ViewContext = shallowReactive({
      viewId: tabView.tabId,
      name: ref(),
      hasPendingChanges: ref(),
      close() {
        closeTab(tabView.tabId)
      },
      navigate(uri: string, options?: ViewNavigateOptions) {

        const [path, querystring] = uri.split('?')

        const query = options?.query ?? {}
        if (querystring) {
          const params = new URLSearchParams(querystring)
          params.forEach((val, key) => {
            query[key] = val
          })
        }
        
        tabView.path = path
        tabView.query = query
        tabView.state = reactive(options?.state ?? {})

        const pane = getPaneFor(tabView.tabId)
        if (pane) {
          pane.selectedTabId = tabView.tabId
        }
      },
    } as ViewContext)
    
    result.name = ref()
    
    watchEffect(() => {
      result.path = tabView.path
      result.query = tabView.query
      result.uri = tabView.path
        
      if (tabView.query) {
        result.uri += '?' + Object.keys(tabView.query).map(r => tabView.query[r] ? r + '=' + tabView.query[r] : r).join('&')
      }
        
      result.state = tabView.state
      result.name.value = tabView.label
      result.hasPendingChanges.value = tabView.hasUnsavedChanges ?? false

      const pane = getPaneFor(tab.tabId)
      result.visible = pane !== undefined && pane.selectedTabId === tab.tabId
      result.active = pane !== undefined && state.activePaneId === pane.paneId && pane.selectedTabId === tab.tabId
    })

    watch(result, () => {
      tabView.state = result.state
      tabView.label = result.name.value ?? ''
      tabView.hasUnsavedChanges = result.hasPendingChanges.value ?? false
    }, {
      deep: true
    })
    
    return shallowReadonly(result)
  }
}

function addPane(idx: number) {
  const width = 100 / (state.panes.length + 1);

  for (const p of state.panes) {
    p.width = width;
  }

  state.panes.splice(idx + 1, 0, {
    paneId: uuidv4(),
    width,
    selectedTabId: 'new',
    tabs: []
  });
}

function closePane(idx: number) {
  const width = 100 / (state.panes.length - 1);

  state.panes.splice(idx, 1);

  for (const p of state.panes) {
    p.width = width;
  }
}

const resizing = ref<number | undefined>();
let startResizeMouse = 0;
let startResizeWidth1 = 0;
let startResizeWidth2 = 0;
let containerWidth: number | undefined = undefined;
const container = ref<Element>();

function beginResize(e: MouseEvent, idx: number) {
  if (!container.value) { return; }

  window.addEventListener('mouseup', stopResize);

  startResizeMouse = e.clientX;
  startResizeWidth1 = state.panes[idx].width;
  startResizeWidth2 = state.panes[idx + 1].width;
  containerWidth = container.value.getBoundingClientRect().width;

  resizing.value = idx;

  window.addEventListener('mousemove', resize);

}

function resize(e: MouseEvent) {
  if (typeof resizing.value === 'undefined' || typeof containerWidth === 'undefined') { return; }

  const delta = e.clientX - startResizeMouse;
  const deltaPerc = delta / containerWidth * 100;

  let newWidth1 = startResizeWidth1 + deltaPerc;
  if (newWidth1 < 5) {
    newWidth1 = 5;
  }
  let newWidth2 = startResizeWidth2 - deltaPerc;
  if (newWidth2 < 5) {
    newWidth2 = 5;
  }

  state.panes[resizing.value].width = newWidth1;
  state.panes[resizing.value + 1].width = newWidth2;
}

function stopResize() {
  window.removeEventListener('mousemove', resize);
  window.removeEventListener('mouseup', stopResize);

  resizing.value = undefined;
}

</script>

<template>
  <div
    class="workspace"
  >
    <WorkspaceSidebar class="workspace__sidebar" />
    
    <div
      class="panes"
      ref="container"
    >
      <template
        v-for="(pane, idx) in state.panes"
        :key="pane.paneId"
      >
        
        <PaneView
          :tabs="pane.tabs[idx]"
          :is-last="idx === state.panes.length - 1"
          :is-first="idx === 0"
          :pane="pane"
          @add-pane="addPane(idx)"
          @close-pane="closePane(idx)"
          class="workspace__pane"
          :style="`width: calc(${pane.width}%${idx !== state.panes.length - 1 ? ' - 1px' : ''})`"
        />
  
        <div
          class="workspace__separator"
          :class="{
              'workspace__separator--resizing': resizing === idx
            }"
          v-if="idx !== state.panes.length - 1"
          @mousedown.prevent.stop="(e) => beginResize(e, idx)"
        ></div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.workspace {
  height: 100%;
  display: grid;
  grid-template-columns: min-content auto;
}

.panes {
  display: flex;
  flex-direction: row;
  height: 100vh;
}

.workspace__separator {
  width: 1px;
  background-color: hsl(var(--color-60));
  position: relative;
  z-index: 100;
}

.workspace__separator:after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  right: -3px;
  cursor: ew-resize;
  background-color: hsla(var(--color-60), 0);
  transition: background-color 200ms;
}

.workspace__separator--resizing:after {
  background-color: hsla(var(--color-60), 1);
}

@media (max-width: 767px) {
  .workspace {
    display: block;
    position: relative;
  }

  .workspace__sidebar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 100;
  }
}
</style>